<template>
  <div v-element-loader="{ endpoint: ['cart/addToCart', 'cart/changeQuantity'], matcher: matchUrlChangeQuantity }" class="cart-product">
    <div v-if="cartRow.machineId || cartRow.machineOwnerId" class="cart-product__machine-connection">
      {{ cartRow.machineOwnerName }}
      <span v-if="cartRow.machineOwnerId && cartRow.machineId"> - </span>
      {{ cartRow.machineName }}
    </div>
    <div class="cart-product__container">
      <oie-base-image
        :images="cartRow.item.image ? `${getAssetPath(cartRow.item.image)}?preset=ProductSmall&watermark=OIEWatermarkSmall` : `${getAssetPath('/globalassets/missing404/404.png')}?preset=ProductThumb`"
        :alt-text="cartRow.item.name"
        image-width="3.75rem"
        image-height="3.75rem"
        class="cart-product__image"></oie-base-image>

      <div class="cart-product__information-container">
        <div class="cart-product__top">
          {{ cartRow.item.name }}

          <div class="cart-product__details-article-number">
            {{ `${tStatic('headerCartItemNumberLabel')} ${cartRow.itemId}` }}
          </div>

          <div v-if="cartRow.stock - cartRow.quantity < 0" class="cart-product__details-exceded-amount">
            {{ tStatic('exceededStockLevelLabel') }}
          </div>
        </div>

        <div class="cart-product__quantity-related">
          <div class="product-quantity">
            <input
              ref="quantity"
              v-model.number="cartRow.quantity"
              class="product-quantity__input">
          </div>

          <div v-if="!isQuoteItem" :class="cartRow.isDiscounted ? 'cart-product__price discounted' : 'cart-product__price'">
            {{ formatPrice(cartRow.price) }} / {{ tStatic('headerCartItemAmountLabel') }}
          </div>
        </div>
      </div>
    </div>

    <p>{{ cartRow.comment }}</p>
  </div>
</template>

<script>
import accordion from '@/mixins/accordion.js'
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import gtm from '@/core/enhanced-ecommerce-gtm'
import cartService from '@/core/cart-service'
import { productStatuses } from '@/variables/product-statuses'

export default {
  mixins: [accordion],
  props: {
    cartRow: {
      type: Object,
      required: true
    },
    cartName: {
      type: String,
      required: true
    },
    useForNotification: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    comment: '',
    commentIsActive: false,
    commentSaveDisabled: true
  }),
  computed: {
    ...mapGetters(['enableGtm']),
    ...mapGetters('myMachines', ['hasMyMachines']),
    isSuperUser() {
      return (
        this.clientModel.impersonating !== null || this.clientModel.isSuperUser
      )
    },
    isQuoteItem() {
      return this.cartRow.publicationStatusCode == productStatuses.noPrice
    }
  },
  methods: {
    ...mapActions('commerce', ['deleteFromCart', 'saveNote', 'setCart']),
    ...mapActions('ui', ['toggleValue']),
    ...mapActions('myMachines', ['getMyMachines']),
    matchUrlChangeQuantity(data) {
      return _.some(data, item => item.itemId === this.cartRow.itemId && !this.cartRow.comment && !this.cartRow.machineId && !this.cartRow.machineOwnerId)
    },
    closeCart() {
      this.toggleValue({ key: 'isCartOpen', data: false })
    },
    deleteItem() {
      const cartRowToDelete = this.cartRow
      this.deleteFromCart({ lineItemId: cartRowToDelete.id }).then(() => {
        if (this.enableGtm) {
          const gtmObject = gtm.createGtmItem(
            cartRowToDelete.item.itemId,
            cartRowToDelete.item.name,
            null,
            cartRowToDelete.item.purchasedOnCategory,
            this.formatPriceWithoutCurrency(cartRowToDelete.priceExVat),
            cartRowToDelete.quantity,
            null)

          gtm.removeFromCart(gtmObject)
        }
      })
    },
    async handleSelection($event) {
      const { data } = await cartService.markMachineForRow($event, this.cartRow.id)
      this.setCart(data.data)
    },
    saveComment(comment, callback) {
      const { id } = this.cartRow
      cartService.saveNote(id, comment).then(() => {
        this.cartRow.comment = comment
        callback.success()
      }).catch(() => callback.error())
    },

    deleteComment(callback) {
      const { id } = this.cartRow
      cartService.deleteNote(id).then(() => {
        this.cartRow.comment = ''
        callback.success()
      }).catch(() => callback.error())
    }
  }
}
</script>

<style lang="scss">
.cart-product {
  padding: measure(quarter);
  border: 1px solid color(border-gray);
  transition: border-color 140ms ease-out, box-shadow 200ms ease-out, background-color 140ms ease-out;
}

.cart-product__actions {
  margin-bottom: measure(quarter);
  flex-wrap: wrap;
}

.cart-product__machine-actions {
  display: flex;
  gap: 0.5rem;
}

.cart-product__machine-actions-cancel {
  padding: 3px;

  > svg:first-child:not(:only-child) {
    margin-right: 0;
  }
}

.cart-product__machine-connection {
  font-style: italic;
  margin-bottom: measure(quarter);
}

.cart-product--new {
  border-color: color(border-gray-2);
  box-shadow: 0px 0px 6px 1px rgba(93, 92, 92, 0.5);
  background-color: color(background-gray-5);
  transition: border-color 140ms ease-in, box-shadow 200ms ease-in, background-color 140ms ease-in;
}

.cart-product__container {
  display: flex;
  flex-wrap: nowrap;
}

.cart-product__image {
  width: 3.75rem;
  height: 3.75rem;
  flex-shrink: 0;
  align-self: flex-start;
}

.cart-product__information-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  margin-left: measure(quarter);
}

.cart-product__top {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin-bottom: measure(quarter);
}

.cart-product__details {
  @include caption-semi-bold;
  color: color(text-blue);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.cart-product__details-article-number {
  color: color(text);
}

.cart-product__details-exceded-amount {
  @include caption-bold;
  margin-top: auto;
}

.cart-product--warning .cart-product__details-exceded-amount {
  align-self: start;
  padding: 0 measure(eighth);
  background-color: color(yellow-2);
  border: 1px solid color(yellow-1);
}

.cart-product__quantity-related {
  display: flex;
  align-items: center;
}

.cart-product__price {
  margin-left: auto;

  &.discounted {
    color:red;
  }
}

.cart-product__comment {
  width: 100%;
  margin-top: measure(half);
}
.cart-product__assemblynote {
  width: 100%;
  margin-top: measure(quarter);
}

.cart-product__quantity-related .product-quantity {
  display: flex;
  align-items: center;
  width: auto;
  padding: measure(eighth) measure(quarter);
  background-color: color(background-blue-5);
}

.cart-product__quantity-related .product-quantity--border {
  display: none;

  @include media(small) {
    display: flex;
    height: 3.125rem;
    border: 2px solid color(border-blue-4);
  }
}

.cart-product__quantity-related .product-quantity--border .product-quantity__input {
  height: 100%;
}

.cart-product__quantity-related .product-quantity__input {
  @include caption-semi-bold;
  width: 1.875rem;
  height: 1.875rem;
  margin: 0 measure(quarter);
  text-align: center;
  background-color: color(background-white);
  border: none;
}

</style>
